import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, inject } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { RouterLink } from '@angular/router';
import { TranslocoModule } from '@jsverse/transloco';
import * as Parse from 'parse';

export interface ActiveCall {
    aircallId: string;
    direction: 'inbound' | 'outbound';
    answeredAt: string;
    phone: string | null;
    name: string | null;
    userId: string | null;
    leadId: string | null;
}

const POLL_MS = 5000;

/**
 * Shows the signed-in staff member's live Aircall call with a link to the
 * caller's Customer 360 (or Lead). Polls getMyActiveCall: admin has no
 * LiveQuery. Polling pauses while the tab is hidden.
 */
@Component({
    selector: 'active-call',
    templateUrl: './active-call.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [MatIconModule, RouterLink, TranslocoModule],
})
export class ActiveCallComponent implements OnInit, OnDestroy {
    private readonly _cdr = inject(ChangeDetectorRef);
    private _poll?: ReturnType<typeof setInterval>;
    private _tick?: ReturnType<typeof setInterval>;
    private _unavailable = false;

    call: ActiveCall | null = null;
    elapsed = '';

    private readonly _onVisibility = () => {
        if (document.hidden) {
            this._stopPolling();
        } else {
            this._startPolling();
        }
    };

    ngOnInit(): void {
        document.addEventListener('visibilitychange', this._onVisibility);
        if (!document.hidden) this._startPolling();
    }

    ngOnDestroy(): void {
        document.removeEventListener('visibilitychange', this._onVisibility);
        this._stopPolling();
        this._setCall(null);
    }

    get link(): string[] | null {
        if (this.call?.userId) return ['/customer-360', this.call.userId];
        if (this.call?.leadId) return ['/leads', this.call.leadId];
        return null;
    }

    private _startPolling(): void {
        if (this._poll || this._unavailable) return;
        void this._refresh();
        this._poll = setInterval(() => void this._refresh(), POLL_MS);
    }

    private _stopPolling(): void {
        if (this._poll) clearInterval(this._poll);
        this._poll = undefined;
    }

    private async _refresh(): Promise<void> {
        try {
            const call = (await Parse.Cloud.run('getMyActiveCall')) as ActiveCall | null;
            this._setCall(call ?? null);
        } catch (error: any) {
            // 141: function not deployed yet, or not an admin. Stop asking
            // until the next page load; network errors keep polling.
            if (error?.code === Parse.Error.SCRIPT_FAILED) {
                this._unavailable = true;
                this._stopPolling();
            }
            this._setCall(null);
        }
    }

    private _setCall(call: ActiveCall | null): void {
        const changed = call?.aircallId !== this.call?.aircallId || call?.userId !== this.call?.userId || call?.leadId !== this.call?.leadId || call?.name !== this.call?.name;
        this.call = call;
        if (call && !this._tick) {
            this._tick = setInterval(() => this._updateElapsed(), 1000);
        } else if (!call && this._tick) {
            clearInterval(this._tick);
            this._tick = undefined;
        }
        this._updateElapsed(changed);
    }

    private _updateElapsed(force = true): void {
        const next = this.call ? formatElapsed(Date.now() - new Date(this.call.answeredAt).getTime()) : '';
        if (!force && next === this.elapsed) return;
        this.elapsed = next;
        this._cdr.markForCheck();
    }
}

export function formatElapsed(ms: number): string {
    const total = Math.max(0, Math.floor(ms / 1000));
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = String(total % 60).padStart(2, '0');
    return h ? `${h}:${String(m).padStart(2, '0')}:${s}` : `${m}:${s}`;
}
